import React, { useEffect, useRef, useState } from 'react'
import './ExplorePlans.css';
import '../../components/Buttons/continueenabled.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { Container } from 'react-bootstrap';
import loader from './loading.json'
import Lottie from 'lottie-react';
import { pushEvent, EVENTS } from '../../utils/dataLayer';
import { paymentSuccessPath } from '../../utils/entryFlow';

const isMainDomain = () => {
    const hostname = window.location.hostname;
    const mainDomain = process.env.REACT_APP_MAIN_DOMAIN || 'dastaan.info';
    return hostname === mainDomain || hostname === `www.${mainDomain}` || process.env.REACT_APP_DOMAIN === 'main';
};

// GA4 ecommerce item shape for a plan.
const toPlanItem = (plan) => ({
    item_id: String(plan.id),
    item_name: plan.plan_name,
    price: plan.plan_price - plan.discount,
    discount: plan.discount,
    item_category: 'subscription',
});

const PlansV2 = () => {
    const fromMainDomain = isMainDomain();
    const [superPacks, setSuperPack] = useState([])
    const [activePlan, setActivePlan] = useState(2);
    const [activePlanAmnt, setActivePlanAmnt] = useState(42);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const MySwal = withReactContent(Swal);
    // Prevents concurrent payment status checks across all three triggers
    const checkingRef = useRef(false);
    // Holds the setInterval ID for background UPI polling
    const pollTimerRef = useRef(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const pendingTxnid = localStorage.getItem('dastaan__pending_txnid');
        if (pendingTxnid) {
            checkPaymentStatus(pendingTxnid);
        } else {
            fetchData();
            getOS();
        }
    }, []);

    // BFCache fix: PayU redirect restores the page from cache on mobile —
    // pageshow with persisted=true fires; useEffect above does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const handlePageShow = (event) => {
            if (event.persisted) {
                const pendingTxnid = localStorage.getItem('dastaan__pending_txnid');
                if (pendingTxnid) checkPaymentStatus(pendingTxnid);
            }
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    // Starts background polling for payment status after returning from a UPI app.
    // Polls every 5s (first call after 3.5s) for up to 60s, then shows plans.
    // Safe to call multiple times — pollTimerRef guards against duplicate intervals.
    const startPaymentPolling = () => {
        if (pollTimerRef.current) return;
        const txnid = localStorage.getItem('dastaan__pending_txnid');
        if (!txnid) return;

        setLoading(true);
        const deadline = Date.now() + 60000;

        const stopPolling = () => {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        };

        const doPoll = () => {
            const t = localStorage.getItem('dastaan__pending_txnid');
            if (!t) { stopPolling(); return; }
            if (Date.now() >= deadline) {
                stopPolling();
                localStorage.removeItem('dastaan__pending_txnid');
                checkingRef.current = false;
                fetchData();
                return;
            }
            checkPaymentStatus(t, true);
        };

        setTimeout(() => {
            doPoll();
            pollTimerRef.current = setInterval(doPoll, 5000);
        }, 3500);
    };

    // Handles stale pending txnid on any future tab-return (e.g. user had
    // an unfinished payment from a previous session and revisits the page).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) startPaymentPolling();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
    }, []);
    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.request({
                method: 'GET',
                maxBodyLength: Infinity,
                timeout: 12000,
                url: `${process.env.REACT_APP_API_URL}/plans`,
                headers: {
                    'Authorization': localStorage.getItem('dastaan__token'),
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
            });
            setSuperPack(response.data.plans);
            pushEvent(EVENTS.VIEW_PLANS, {
                ecommerce: {
                    item_list_name: 'plans',
                    items: (response.data.plans || []).map(toPlanItem),
                },
            });
        } catch (err) {
            // silent fail — show empty plan list
        } finally {
            setLoading(false);
        }
    };
    const getOS = () => {
        const userAgent = window.navigator.userAgent;
        const platform = window.navigator.platform;
        const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
        const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
        const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
        let os = null;
        if (macosPlatforms.includes(platform)) {
            os = 'Mac OS';
        } else if (iosPlatforms.includes(platform)) {
            os = 'iOS';
        } else if (windowsPlatforms.includes(platform)) {
            os = 'Windows';
        } else if (/Android/.test(userAgent)) {
            os = 'Android';
        } else if (!os && /Linux/.test(platform)) {
            os = 'Linux';
        }
        return os;
    };
    const createPaymentMandate = async () => {
        if (isSubmitting) return;
        const current_plan_amnt = parseInt(localStorage.getItem('dastaan__plan_amnt'))
        if (current_plan_amnt > 0) {
            if (current_plan_amnt === activePlanAmnt && localStorage.getItem('is_subscription_cancelled') === 'false') {
                Swal.fire({
                    title: "Plan is already purchased",
                    background: "#000"
                });
            }
            else if (current_plan_amnt < activePlanAmnt) {
                MySwal.fire({
                    title: 'Are you sure want to upgrade the plan?',
                    background: "#000",
                    // text: 'You cannot undo this action.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                }).then((result) => {
                    if (result.isConfirmed) {
                        checkoutPayment()
                    }
                });
                //  confirm('.')
            }
            else if (current_plan_amnt > activePlanAmnt) {
                MySwal.fire({
                    title: 'Are you sure want to downgrade the plan?',
                    // text: 'You cannot undo this action.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                }).then((result) => {
                    if (result.isConfirmed) {
                        checkoutPayment()
                    }
                });
                //  confirm('Are you sure want to downgrade the plan.')
            }
            else {
                checkoutPayment()
            }
        }
        else {
            checkoutPayment()
        }
    }

    // Added By: Pradyumn Dwivedi; Added on: 29 July 2024; Use: To initialize Facebook Pixel for tracking events
    useEffect(() => {
        // Initialize Facebook Pixel with your Pixel ID
        if (window.fbq) {
            window.fbq('init', '1310440030053294');
            window.fbq('track', 'PageView');
        }
    }, []);

    // Added By: Pradyumn Dwivedi; Added on: 29 July 2024; Use: To track a purchase event
    const handlePurchase = () => {
        if (window.fbq) {
            window.fbq('track', 'Purchase', {
                value: 142.52,
                currency: 'USD'
            });
        }
    };

    const checkPaymentStatus = (txnid, isPolling = false) => {
        if (checkingRef.current) return;
        checkingRef.current = true;
        setLoading(true);
        const data = JSON.stringify({
            user_id: parseInt(localStorage.getItem('dastaan__user_id')),
            txnid: txnid,
        });
        axios.request({
            method: 'POST',
            url: `${process.env.REACT_APP_API_URL}/payu-payment-status`,
            timeout: 15000,
            headers: {
                'Authorization': localStorage.getItem('dastaan__token'),
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            data: data
        }).then((response) => {
            checkingRef.current = false;
            localStorage.removeItem('dastaan__pending_txnid');
            const expDate = response?.data?.exp_date;
            if (expDate != null && expDate !== undefined) {
                const planId = response?.data?.plan_id;
                const planAmnt = response?.data?.plan_amnt || response?.data?.plan_amount;
                if (planId) localStorage.setItem('dastaan__plan_id', planId);
                if (planAmnt) localStorage.setItem('dastaan__plan_amnt', planAmnt);
                localStorage.setItem('is_subscription_cancelled', 'false');
                // Content gates across the app read dastaan__plan_active, which is
                // otherwise only written by the /home and audio responses. Set it here
                // so access works on the very next render instead of after a reload.
                localStorage.setItem('dastaan__plan_active', 'true');
                // Land on a real URL so ad platforms can track the conversion.
                // PaymentSuccess owns the confirmation UI and the handoff to
                // the main domain from here on.
                const successParams = new URLSearchParams({
                    txnid: txnid || '',
                    amount: planAmnt || localStorage.getItem('dastaan__plan_amnt') || '',
                });
                window.location.href = `${paymentSuccessPath()}?${successParams.toString()}`;
            } else if (isPolling) {
                // Payment still pending — release the lock so the next poll can retry
                checkingRef.current = false;
            } else {
                localStorage.removeItem('dastaan__pending_txnid');
                fetchData();
            }
        }).catch(() => {
            checkingRef.current = false;
            if (!isPolling) {
                localStorage.removeItem('dastaan__pending_txnid');
                fetchData();
            }
            // isPolling: keep txnid, let the interval retry on next tick
        });
    };

    const checkoutPayment = () => {
        setIsSubmitting(true);
        const selectedPlan = superPacks.find(p => p.id === activePlan);
        pushEvent(EVENTS.BEGIN_CHECKOUT, {
            ecommerce: {
                currency: 'INR',
                value: activePlanAmnt,
                items: selectedPlan ? [toPlanItem(selectedPlan)] : [{ item_id: String(activePlan), price: activePlanAmnt }],
            },
        });
        setLoading(true)
        const data = JSON.stringify({
            'plan_id': activePlan,
            'user_id': localStorage.getItem('dastaan__user_id'),
        });
        let config = {
            method: 'POST',
            maxBodyLength: Infinity,
            url: `${process.env.REACT_APP_API_URL}/payu-create-intent`,
            headers: {
                'Authorization': localStorage.getItem('dastaan__token'),
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            data: data
        };
        axios.request(config).then((response) => {
            if (response.data.data !== undefined) {
                handlePurchase();
                const redirectUrl = response?.data?.data?.redirect_url ?? null;
                const txnid = response?.data?.data?.txnid ?? null;
                if (txnid) localStorage.setItem('dastaan__pending_txnid', txnid);
                window.location.href = redirectUrl;

                // When user returns from UPI app, auto-check payment status.
                // visibilitychange fires when the tab comes to foreground;
                // window focus is a fallback for browsers that skip visibilitychange.
                const onReturn = () => {
                    if (document.hidden) return;
                    document.removeEventListener('visibilitychange', onReturn);
                    window.removeEventListener('focus', onReturn);
                    startPaymentPolling();
                };
                document.addEventListener('visibilitychange', onReturn);
                window.addEventListener('focus', onReturn);
            } else {
                setIsSubmitting(false);
                setLoading(false);
            }
        }).catch(() => {
            setIsSubmitting(false);
            setLoading(false);
        })
    }
    /*
        const [superPacks, setSuperPack] = useState([])
        const [activePlan, setActivePlan] = useState(1);
        // const [mandateURL, setMandateUrl] = useState(null);
        const [paybutton, setShowPayButton] = useState(false)
        const [show, setShow] = useState(false);
        const handleClose = () => setShow(false);
        // const handleShow = () => setShow(true);
        const [vpa, setVPA] = useState(null);
        useEffect(() => {
            fetchData();
            const os = getOS()
            console.log(os);
        }, []);
        const getOS = () => {
            const userAgent = window.navigator.userAgent;
            const platform = window.navigator.platform;
            const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
            const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
            const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
            let os = null;
            if (macosPlatforms.includes(platform)) {
                os = 'Mac OS';
            } else if (iosPlatforms.includes(platform)) {
                os = 'iOS';
            } else if (windowsPlatforms.includes(platform)) {
                os = 'Windows';
            } else if (/Android/.test(userAgent)) {
                os = 'Android';
            } else if (!os && /Linux/.test(platform)) {
                os = 'Linux';
            }
            return os;
        };
        const fetchData = async () => {
            setLoading(true)
            try {
                const data = JSON.stringify({
                    'phone': localStorage.getItem('dastaan__phone') !== null ? localStorage.getItem('dastaan__phone') : 1
                });
                let config = {
                    method: 'GET',
                    maxBodyLength: Infinity,
                    url: `${process.env.REACT_APP_API_URL}/plans`,
                    headers: {
                        'Authorization': localStorage.getItem('dastaan__token'),
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    data: data
                };
                axios.request(config)
                    .then((response) => {
                        // console.log(response.data);
                        setLoading(false)
                        setSuperPack(response.data.plans)
                    });
            } catch (err) {
                setLoading(false)
                // setError(err);
            } finally {
                // setLoading(false);
            }
        };
        const handleVPA = (event) => {
            setVPA(event.target.value);
        }
        const createPaymentMandate = async () => {
            setLoading(true)
            const data = JSON.stringify({
                'plan_id': activePlan,
                'user_id': localStorage.getItem('dastaan__user_id'),
            });
            let config = {
                method: 'POST',
                maxBodyLength: Infinity,
                url: `${process.env.REACT_APP_API_URL}/payu-create-intent`,
                headers: {
                    'Authorization': localStorage.getItem('dastaan__token'),
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                data: data
            };
            axios.request(config).then((response) => {
                if (response.data.data !== undefined) {
                    // setShowPayButton(true)
                    const redirectUrl = response?.data?.data?.redirect_url !== undefined ? response?.data?.data?.redirect_url : null;
                    const txnid = response?.data?.data?.txnid !== undefined ? response?.data?.data?.txnid : null;
                    console.log(txnid);
                    window.location.href = redirectUrl
                    const postdata = JSON.stringify({
                        'user_id':localStorage.getItem('dastaan__user_id'),
                        'txnid': txnid ?? null,
                    });
                    setTimeout(() => {
                        setLoading(false)
                        window.location.href = '/';
                    }, 10000);
                }
            })
        }
        const changePlan = (id) => {
            setActivePlan(id)
        }
        const handleWebPayment = (e) => {
            e.preventDefault();
            const formData = new FormData();
            formData.append('plan_id', activePlan)
            formData.append('mobileNumber', localStorage.getItem('dastaan__phone'))
            formData.append('subscriptionName', 'Daily')
            formData.append('merchantUserId', localStorage.getItem('dastaan__user_id'))
            formData.append('user_id', localStorage.getItem('dastaan__user_id'))
            formData.append('upi_id', vpa)
            const config = {
                method: 'POST',
                maxBodyLength: Infinity,
                url: `${process.env.REACT_APP_API_URL}/create-phonepe-subscription-vpa`,
                // url: `https://tamashatv.com/create_user_subscription`,
                headers: {
                    'Authorization': localStorage.getItem('dastaan__token'),
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                data: formData
            }
            axios.request(config).then((response) => {
                console.log(response);
                const msg = response.data.message;
                setShow(false)
                toast(msg);
                setVPA(null)
            })
            //
        }
        const handleVPAVerification = async () => {
            console.log(vpa);
            const baseURL = 'https://api.phonepe.com/apis/hermes';
            const endpoint = '/v1/vpa/validate';
            const apiKey = '4865f6b1-9e26-47ee-bc4f-b1febdff78e5'; // Replace with your API key
            const saltKey = '1'; // Replace with your salt key
            const merchantId = 'TAMASHATVONLINE'; // Replace with your merchant ID
            const vpaToVerify = vpa;
            const payload = {
                merchantId,
                //   transactionId: 'UNIQUE_TRANSACTION_ID', // Generate a unique transaction ID
                vpa: vpaToVerify
            };
            const payloadString = JSON.stringify(payload);
            const checksum = crypto.createHash('sha256').update(payloadString + saltKey).digest('hex');
            try {
                const response = await axios.post(`${baseURL}${endpoint}`, payload, {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-VERIFY': `${checksum}###${saltKey}`,
                        'accept': 'application/json',
                        'X-CLIENT-ID': apiKey
                    }
                });
                if (response.data.success) {
                    console.log('VPA is valid');
                    // Handle success case
                } else {
                    console.log('VPA is invalid');
                    // Handle failure case
                }
            } catch (error) {
                console.error('Error verifying VPA:', error.response ? error.response.data : error.message);
                // Handle error case
            }
        }
    */
    return (
        <>
            {
                loading === false ? (
                    <>
                        <Container className='plan-section' style={{
                            minHeight: '100vh',
                            width: '100%',
                            backgroundImage: `url(${process.env.PUBLIC_URL}/images/${
                                window.location.hostname === 'login.dastaan.info'
                                    ? 'modal-bg-login.jpg'
                                    : 'modal-bg.jpg'
                            })`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            position: 'absolute'
                        }}>

                            <div className="plans-container">
                                <div style={{ display: 'flex', justifyContent: 'flex-end', minHeight: '28px', marginBottom: '4px' }}>
                                    {fromMainDomain && (
                                        <button
                                            onClick={() => { window.location.href = process.env.REACT_APP_MAIN_DOMAIN_URL || `https://${process.env.REACT_APP_MAIN_DOMAIN || 'preprod.dastaan.info'}/`; }}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: 'rgba(255,255,255,0.75)',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                letterSpacing: '0.3px',
                                                padding: '4px 2px',
                                            }}
                                        >
                                            Skip &rsaquo;
                                        </button>
                                    )}
                                </div>
                                <h2 className="heading">EXPLORE PLANS</h2>
                                {
                                    superPacks.map(plan => {
                                        const amnt = plan.plan_price - plan.discount;
                                        return (
                                            <div
                                                key={plan.id}
                                                className={`plan-box ${activePlan === plan.id ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setActivePlan(plan.id);
                                                    setActivePlanAmnt(amnt);
                                                    pushEvent(EVENTS.SELECT_PLAN, { ecommerce: { item_list_name: 'plans', items: [toPlanItem(plan)] } });
                                                }} >
                                                <div className="left-section">
                                                    <div className="radio-btn">
                                                        <div className={`dot ${activePlan === plan.id ? 'active' : ''}`} />
                                                    </div>
                                                    <span className="plan-name">{plan.plan_name}</span>
                                                    {plan.id === 2 && <span className="recommended-tag">Recommended</span>}
                                                </div>
                                                <div className="right-section">
                                                    <div className="price-wrapper">
                                                        <div className="price">
                                                            ₹ <span className="bold">{(plan.plan_price) - plan.discount}</span>{' '}
                                                            <span className="striked">₹{plan.plan_price}</span>
                                                        </div>
                                                        <div className={`${activePlan === plan.id && 'off-badge-selected'} off-badge`}>
                                                            <strong>{plan.discount_percent} %</strong>
                                                            <span>Off</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                <button
                                    className="pay-now"
                                    onClick={createPaymentMandate}
                                    disabled={isSubmitting}
                                    style={isSubmitting ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                >
                                    {isSubmitting ? 'Please wait...' : 'Pay Now'}
                                </button>
                                {/* {fromMainDomain && (
                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                                        <button
                                            className='skip-btn'
                                            style={{ maxWidth: '280px' }}
                                            onClick={() => { window.location.href = process.env.REACT_APP_MAIN_DOMAIN_URL || `https://${process.env.REACT_APP_MAIN_DOMAIN || 'preprod.dastaan.info'}/`; }}
                                        >
                                            Skip for now
                                        </button>
                                    </div>
                                )} */}
                            </div>
                        </Container>
                    </>
                ) : (
                    <>
                        <div className='center'>
                            <Lottie animationData={loader} loop={true}
                                className='loader'
                            />
                        </div>
                    </>
                )
            }
        </>
    )
}
export default PlansV2
